import { HHBox, HHTypography } from '@hinge-health/react-component-library';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { IngestionStats } from '../types';
import { formatCount } from '../utils/format.utils';
import { SurfaceCard } from './surface-card';

export type ThroughputMeasureKey =
  | 'rowsProcessed'
  | 'filesReceived'
  | 'needsAttention';

export const THROUGHPUT_PANEL_TEST_ID = 'pdg-throughput-panel';
export const THROUGHPUT_PANEL_LABEL = 'Throughput, last 24 hours';
export const throughputMeasureTestId = (key: ThroughputMeasureKey): string =>
  `pdg-throughput-measure-${key}`;

interface Measure {
  key: ThroughputMeasureKey;
  label: string;
  value: string;
  detail?: string;
}

const buildMeasures = (stats: IngestionStats): Measure[] => {
  const filesReceived =
    stats.completedCount + stats.failedCount + stats.inFlightCount;

  return [
    {
      key: 'rowsProcessed',
      label: 'Rows processed',
      value: formatCount(stats.totalRowsParsed),
    },
    {
      key: 'filesReceived',
      label: 'Files received',
      value: formatCount(filesReceived),
      detail: `${formatCount(stats.completedCount)} completed · ${formatCount(
        stats.failedCount,
      )} failed · ${formatCount(stats.inFlightCount)} in flight`,
    },
    {
      key: 'needsAttention',
      label: 'Needs attention',
      value: formatCount(stats.failedCount),
      detail: 'Failed or rejected files',
    },
  ];
};

interface ThroughputPanelProps {
  stats: IngestionStats;
}

/**
 * Three headline measures for the summary window. Sparklines wait on
 * per-day buckets from PDGS; until then the panel is numbers only.
 */
export const ThroughputPanel = ({
  stats,
}: ThroughputPanelProps): JSX.Element => (
  <SurfaceCard
    role="region"
    aria-label={THROUGHPUT_PANEL_LABEL}
    data-testid={THROUGHPUT_PANEL_TEST_ID}
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
      overflow: 'hidden',
    }}
  >
    {buildMeasures(stats).map((measure, index) => (
      <HHBox
        key={measure.key}
        data-testid={throughputMeasureTestId(measure.key)}
        sx={(theme: Theme): SystemStyleObject<Theme> => ({
          paddingX: 6,
          paddingY: 5,
          borderTop: {
            xs: index > 0 ? `1px solid ${theme.palette.divider}` : 'none',
            md: 'none',
          },
          borderLeft: {
            md: index > 0 ? `1px solid ${theme.palette.divider}` : 'none',
          },
        })}
      >
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
        >
          {measure.label}
        </HHTypography>
        <HHTypography
          hhVariant="variant-bypass"
          variant="h4"
          component="p"
          sx={{
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            marginTop: 1,
          }}
        >
          {measure.value}
        </HHTypography>
        {measure.detail && (
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="text.secondary"
            sx={{ marginTop: 1.5 }}
          >
            {measure.detail}
          </HHTypography>
        )}
      </HHBox>
    ))}
  </SurfaceCard>
);
